import React, { useContext } from 'react';
import styled from 'styled-components';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import { breakpoints, gridContainer } from 'styles';
import { LocalContext } from 'context';
import { motion } from 'framer-motion';
import FB from 'assets/svgs/fb-icon.svg';
import Email from 'assets/svgs/email-icon.svg';
import IN from 'assets/svgs/linkedin-icon.svg';
import TW from 'assets/svgs/twitter-icon.svg';
import BG1 from 'assets/svgs/bg-moon-1.svg';
import BG2 from 'assets/svgs/bg-moon-2.svg';
import BG3 from 'assets/svgs/broken-circle.svg';

const SpeakerTopicOverview = ({ data }) => {
  const { mobile, theme } = useContext(LocalContext);

  return (
    <Backdrop>
      <Container>
        <Slide theme={theme}>
          <SpeakerImgContainer>
            <SpeakerImg image={getImage(data.image.childImageSharp)} cropped />
            <SVG initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <Circle1 />
            </SVG>
            <SVG initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <Circle2 />
            </SVG>
            <SVG initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <Circle3 />
            </SVG>
          </SpeakerImgContainer>
          <SpeakerDetails>
            <SpeakerName>{data.name}</SpeakerName>
            {data.authors && (
              <SpeakerTitle>
                {data.authors}
                <br />
                <span>{data.college}</span>
              </SpeakerTitle>
            )}
            {data.positions && (
              <SpeakerPositions>
                {data.positions.map((position) => (
                  <p>{position}</p>
                ))}
              </SpeakerPositions>
            )}
            {data.colleges && (
              <SpeakerColleges>
                {data.colleges.map((college) => (
                  <p>{college}</p>
                ))}
              </SpeakerColleges>
            )}

            {data.linkedinLink && (
              <SpeakerSocials>
                {data.fbLink && (
                  <SpeakerSocial href={data.fbLink} theme={theme} target="blank">
                    <FB />
                  </SpeakerSocial>
                )}
                {data.linkedinLink && (
                  <SpeakerSocial href={data.linkedinLink} theme={theme} target="blank">
                    <IN />
                  </SpeakerSocial>
                )}
                {data.twLink && (
                  <SpeakerSocial href={data.twLink} theme={theme} target="blank">
                    <TW />
                  </SpeakerSocial>
                )}
                {data.emailLink && (
                  <SpeakerSocial href={data.emailLink} theme={theme}>
                    <Email />
                  </SpeakerSocial>
                )}
              </SpeakerSocials>
            )}

            <SpeakerTopics>
              {data.topics.map((topic, i) => (
                <SpeakerTopic key={i}>{topic}</SpeakerTopic>
              ))}
            </SpeakerTopics>
            <SpeakerSummary theme={theme} dangerouslySetInnerHTML={{ __html: data.summary }} />
          </SpeakerDetails>
        </Slide>
      </Container>
    </Backdrop>
  );
};

const Backdrop = styled.div`
  width: auto;
  height: auto;
  @media (max-width: 1150px) {
    overflow-x: hidden;
  }
`;

const Container = styled(motion.div)`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 1200px;
  max-width: 100%;
  padding-top: 1rem;
  @media (min-width: 768px) {
    max-width: 400px;
  }
  @media (min-width: 1150px) {
    max-width: 100%;
    padding-top: 0;
  }
`;

const Slide = styled.div`
  position: relative;
  ${gridContainer}
  max-width: 100%;
  width: 1200px;
  background: ${({ theme }) => theme.bgLight};
  height: auto;
  padding: 1.25rem;
  align-items: center;
  margin-bottom: 3rem;
  margin-top: 10rem;
  color: ${({ theme }) => theme.textColor};
  @media (min-width: ${breakpoints.lg}) {
    margin-top: 0px;
    margin-left: 200px;
    margin-right: 5rem;
  }
`;

const Prev = styled.div`
  outline: none;
  border: none;
  cursor: pointer;
  width: 3rem;
  height: 3rem;
  display: none;
  justify-content: center;
  align-items: center;
  border-top-left-radius: 50%;
  border-bottom-left-radius: 50%;
  justify-self: end;
  background-color: ${({ theme }) => theme.fg};
  padding-left: 0.625rem;
  transition: 0.5s;
  opacity: 0.8;
  position: absolute;
  left: -4.5rem;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  svg {
    path {
      stroke: ${({ theme }) => theme.bgLight};
    }
  }
  :hover {
    transform: translateY(-50%) scale(1.05);
    opacity: 1;
  }
  @media (min-width: ${breakpoints.lg}) {
    display: flex;
  }
`;
const Next = styled.div`
  outline: none;
  border: none;
  cursor: pointer;
  width: 3rem;
  height: 3rem;
  display: none;
  justify-content: center;
  align-items: center;
  border-top-right-radius: 50%;
  border-bottom-right-radius: 50%;
  justify-self: start;
  background-color: ${({ theme }) => theme.fg};
  padding-right: 0.625rem;
  transition: 0.5s;
  opacity: 0.8;
  position: absolute;
  right: -4.5rem;
  svg {
    path {
      stroke: ${({ theme }) => theme.bgLight};
    }
  }
  :hover {
    transform: scale(1.05);
    opacity: 1;
  }
  @media (min-width: ${breakpoints.lg}) {
    display: flex;
  }
`;

const SpeakerImgContainer = styled.div`
  position: relative;
  grid-column: 1/7;
  margin-bottom: 1.25rem;
  margin-top: -50%;
  margin-left: -1.25rem;
  margin-right: -1.25rem;
  width: 300px;
  max-width: 100%;
  justify-self: center;

  @media (min-width: ${breakpoints.lg}) {
    height: auto;
    width: 300px;
    max-width: 100%;
    margin-top: 0px;
    grid-column: 1/5;
    align-self: start;
    margin-left: -50%;
    margin-right: 50%;
  }
`;

const SpeakerImg = styled(GatsbyImage)`
  position: relative;
  z-index: 2;
  width: 100%;
  height: auto;
  object-fit: cover;
  object-position: center;
  border-radius: ${({ cropped }) => (cropped ? '50%' : '0px')};
  transition: 0.5s;
  cursor: pointer;
  box-shadow: 0px 0.375rem 0.75rem rgba(0, 0, 0, 0.25);
`;

const SpeakerName = styled.h3`
  font-style: normal;
  font-weight: 700;
  font-size: 1.5rem;
  margin-top: 0px;
  margin-bottom: 0px;
  text-align: left;
`;

const SpeakerTitle = styled.p`
  margin-top: 0px;
  font-style: normal;
  font-weight: 400;
  font-size: 0.75rem;
  line-height: 1.5em;
  margin-bottom: 1.5em;
  text-align: left;
  font-weight: 700;
  span {
    font-weight: normal;
  }
`;

const SpeakerSocials = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin-bottom: 2rem;
`;

const SpeakerSocial = styled.a`
  margin-right: 0.625rem;
  svg {
    circle {
      fill: ${({ theme }) => theme.textColor};
      transition: 0.5s;
    }
    path {
      fill: ${({ theme }) => theme.bgLight};
      transition: 0.5s;
    }
  }
  :hover {
    svg {
      circle {
        fill: ${({ theme }) => theme.fg};
      }
      path {
        fill: ${({ theme }) => theme.bgLight};
      }
    }
  }
`;

const SpeakerTopics = styled.p`
  font-size: 1.25rem;
  margin-bottom: 1rem;
  margin-top: auto;
`;

const SpeakerColleges = styled.div`
  margin-top: 0.5rem;
  margin-top: 0.5rem;
  p {
    font-size: 0.75rem;
    font-weight: 500;
    margin-bottom: 0.25rem;
    margin-top: auto;
    @media (min-width: 48em) {
      font-size: 0.875rem;
    }
  }
`;

const SpeakerPositions = styled.div`
  margin-top: 0.5rem;
  margin-top: 0.5rem;
  p {
    font-size: 0.75rem;
    font-weight: 500;
    margin-bottom: 0.25rem;
    margin-top: auto;
    @media (min-width: 48em) {
      font-size: 0.875rem;
    }
  }
`;

const SpeakerTopic = styled.span`
  margin-top: auto;
  margin-bottom: 0;
  margin-right: 0.375rem;
  font-size: 1.5rem;
  line-height: 2rem;
  &:after {
    content: ',';
  }
  &:last-child {
    &:after {
      content: '';
    }
  }
`;

const SpeakerSummary = styled.div`
  font-style: normal;
  font-weight: 400;
  font-size: 1rem;
  line-height: 1.5rem;
  overflow-wrap: break-word;
`;

const SpeakerDetails = styled.div`
  grid-column: 1/7;
  margin-left: 0px;
  display: flex;
  flex-direction: column;

  @media (min-width: ${breakpoints.lg}) {
    margin-left: -180px;
    padding: 2.5rem;
    grid-column: 5/11;
  }
`;

const SVG = styled(motion.div)`
  opacity: 1;
  width: auto;
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  bottom: 0;
  z-index: 0;
  transition: 1s;
  :nth-child(5) {
    z-index: 5;
  }
  svg {
    path {
      fill: ${({ theme }) => `${theme.fg} !important`};
      transition: 0.5s;
    }
  }
`;

const Circle1 = styled(BG1)`
  position: absolute;
  left: -15%;
  top: -15%;
  width: 100%;
  height: 100%;
  transform: rotate(60deg);
`;
const Circle2 = styled(BG2)`
  position: absolute;
  right: -15%;
  bottom: -15%;
  width: 100%;
  height: 100%;
  transform: rotate(60deg);
`;
const Circle3 = styled(BG3)`
  position: absolute;
  left: -10%;
  bottom: -15%;
  width: 50%;
  height: 50%;
`;

export default SpeakerTopicOverview;
